import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Facebook, Instagram, ExternalLink } from 'lucide-react';

const Footer = ({ variant = 'full' }) => {
  const { t, language } = useLanguage();

  if (variant === 'simple') {
    return (
      <footer className="bg-gray-50 border-t border-gray-200 py-6 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-500">
              © {new Date().getFullYear()} Middleman. {t('landing.allRightsReserved')}
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-500">{language === 'fr' ? 'Réalisé par' : 'Made by'}</span>
              <a href="https://www.lesamouraiduweb.com" target="_blank" rel="noopener noreferrer" className="text-brand-700 hover:text-brand-800 font-semibold transition-colors flex items-center gap-1">
                Le Samouraï du Web
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  const linkClass = "text-gray-600 hover:text-gray-900 text-sm transition-colors";

  return (
    <footer className="bg-gray-50 border-t border-gray-200 text-gray-700 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-12 mb-12">
          <div className="col-span-2 md:col-span-1">
            <img src="/logo-dark-cropped.png" alt="Middleman" className="h-7 md:h-8 w-auto mb-5" />
            <p className="text-gray-600 text-sm leading-relaxed mb-6 max-w-xs">
              {t('landing.footerTagline')}
            </p>
            <div className="flex gap-2">
              <a href="https://www.facebook.com/middleman.secure" target="_blank" rel="noopener noreferrer" className="p-2 bg-white hover:bg-gray-100 border border-gray-200 text-gray-700 hover:text-brand-700 rounded-full transition-all" aria-label="Facebook">
                <Facebook className="h-4 w-4" />
              </a>
              <a href="https://www.instagram.com/middleman.secure" target="_blank" rel="noopener noreferrer" className="p-2 bg-white hover:bg-gray-100 border border-gray-200 text-gray-700 hover:text-brand-700 rounded-full transition-all" aria-label="Instagram">
                <Instagram className="h-4 w-4" />
              </a>
              <a href="https://m.me/466226189896489" target="_blank" rel="noopener noreferrer" className="p-2 bg-white hover:bg-gray-100 border border-gray-200 text-gray-700 hover:text-brand-700 rounded-full transition-all" aria-label="Messenger">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.936 1.444 5.544 3.746 7.256v3.501l3.373-1.851c.9.25 1.851.384 2.881.384 5.523 0 10-4.145 10-9.243S17.523 2 12 2zm1.093 12.441l-2.545-2.72-4.968 2.72 5.467-5.803 2.609 2.72 4.905-2.72-5.468 5.803z" />
                </svg>
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-gray-900 text-sm font-semibold mb-4">{t('landing.quickLinks')}</h4>
            <ul className="space-y-2.5">
              <li><Link to="/" className={linkClass}>{language === 'fr' ? 'Accueil' : 'Home'}</Link></li>
              <li><Link to="/events" className={linkClass}>{language === 'fr' ? 'Événements' : 'Events'}</Link></li>
              <li><Link to="/faq" className={linkClass}>{t('landing.faq')}</Link></li>
              <li><Link to="/contact" className={linkClass}>{t('landing.contact')}</Link></li>
              <li><Link to="/fraudes" className={linkClass}>{language === 'fr' ? 'Cas de fraudes' : 'Fraud Cases'}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-gray-900 text-sm font-semibold mb-4">{language === 'fr' ? 'Explorer' : 'Explore'}</h4>
            <ul className="space-y-2.5">
              <li><Link to="/facebook-groups" className={linkClass}>{language === 'fr' ? 'Trouver des billets' : 'Find Tickets'}</Link></li>
              <li><Link to="/guides-plateformes" className={linkClass}>{language === 'fr' ? 'Guides des plateformes' : 'Platform Guides'}</Link></li>
              <li><Link to="/anti-fraude" className={linkClass}>{language === 'fr' ? 'Anti-fraude' : 'Anti-Fraud'}</Link></li>
              <li><Link to="/statistiques-fraude" className={linkClass}>{language === 'fr' ? 'Statistiques fraude' : 'Fraud Statistics'}</Link></li>
              <li><Link to="/devenir-partenaire" className={linkClass}>{language === 'fr' ? 'Devenir Partenaire' : 'Become a Partner'}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-gray-900 text-sm font-semibold mb-4">{t('landing.legal')}</h4>
            <ul className="space-y-2.5">
              <li><Link to="/conditions-utilisation" className={linkClass}>{language === 'fr' ? "Conditions d'utilisation" : 'Terms of Service'}</Link></li>
              <li><Link to="/politique-confidentialite" className={linkClass}>{language === 'fr' ? 'Politique de confidentialité' : 'Privacy Policy'}</Link></li>
              <li><Link to="/mes-donnees" className={linkClass}>{language === 'fr' ? 'Gestion de mes données' : 'Manage My Data'}</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
          <p className="text-gray-500">© {new Date().getFullYear()} Middleman. {t('landing.allRightsReserved')}</p>
          <div className="flex items-center gap-2 text-gray-500">
            <span>{language === 'fr' ? 'Réalisé par' : 'Made by'}</span>
            <a href="https://www.lesamouraiduweb.com" target="_blank" rel="noopener noreferrer" className="text-brand-700 hover:text-brand-800 font-semibold transition-colors inline-flex items-center gap-1">
              Le Samouraï du Web
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
